import { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router";
import { useDraggable, useDroppable, type DragEndEvent } from "@dnd-kit/core";
import {
  Folder,
  Info,
  Network,
  Pencil,
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  Copy,
} from "lucide-react";
import type { FolderNode } from "#shared/schemas/folder.ts";
import {
  ConfirmDialog,
  InlineRenameInput,
} from "@web/components/ConfirmDialog.tsx";
import { PlaylistThumb } from "@web/components/PlaylistThumb.tsx";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@web/components/ui/context-menu.tsx";
import { useOpenDetail } from "@web/features/detail/useOpenDetail.ts";
import { nextDefaultName } from "@web/lib/names.ts";
import { Button } from "@web/components/ui/button.tsx";
import { cn } from "@web/lib/utils.ts";
import {
  useCreateFolder,
  useCreateLocalPlaylist,
  useDeleteFolder,
  useDeleteLocalPlaylist,
  useDuplicateFolder,
  useDuplicateLocalPlaylist,
  useFolders,
  useLocalPlaylists,
  useMoveFolder,
  useMoveLocalPlaylist,
  useRenameFolder,
  useRenameLocalPlaylist,
  useSetFolderCollapsed,
} from "@web/features/library/api.ts";
import {
  ROOT_DROP_ID,
  folderDragId,
  folderDropId,
  playlistDragId,
  playlistDropId,
  useLibraryDnd,
  type DropIntent,
  type WorkspaceDrag,
} from "./libraryDnd.tsx";

type Editing =
  | { kind: "folder" | "playlist"; id: string; draft: string; original: string }
  | null;

type PendingDelete =
  | { kind: "folder"; id: string; name: string }
  | { kind: "playlist"; id: string; name: string }
  | null;

function flattenFolderNames(nodes: FolderNode[]): string[] {
  const out: string[] = [];
  for (const n of nodes) {
    out.push(n.name);
    out.push(...flattenFolderNames(n.children));
  }
  return out;
}

function findFolder(nodes: FolderNode[], id: string): FolderNode | null {
  for (const n of nodes) {
    if (n.id === id) return n;
    const hit = findFolder(n.children, id);
    if (hit) return hit;
  }
  return null;
}

function descendantIds(node: FolderNode): Set<string> {
  const ids = new Set<string>();
  const walk = (n: FolderNode) => {
    for (const child of n.children) {
      ids.add(child.id);
      walk(child);
    }
  };
  walk(node);
  return ids;
}

function nestPad(level: number): React.CSSProperties | undefined {
  if (level <= 0) return undefined;
  return { paddingLeft: `calc(var(--spacing) * ${level * 3})` };
}

function resolveWorkspaceIntent(
  overId: string,
  pointerY: number,
  overTop: number,
  overHeight: number,
  target:
    | { kind: "folder"; id: string; parentId: string | null }
    | { kind: "playlist"; id: string; folderId: string | null }
    | undefined,
  draggingTracks: boolean,
): DropIntent | null {
  if (overId === ROOT_DROP_ID) return { kind: "root" };
  if (overId.startsWith("drop:playlist:")) {
    const playlistId = overId.slice("drop:playlist:".length);
    if (draggingTracks) return { kind: "tracks-onto-playlist", playlistId };
    if (!target || target.kind !== "playlist") {
      return {
        kind: "after",
        parentId: null,
        afterId: playlistId,
        targetKind: "playlist",
      };
    }
    if (overHeight <= 0) {
      return {
        kind: "after",
        parentId: target.folderId,
        afterId: target.id,
        targetKind: "playlist",
      };
    }
    const rel = (pointerY - overTop) / overHeight;
    if (rel < 0.5) {
      return {
        kind: "before",
        parentId: target.folderId,
        beforeId: target.id,
        targetKind: "playlist",
      };
    }
    return {
      kind: "after",
      parentId: target.folderId,
      afterId: target.id,
      targetKind: "playlist",
    };
  }
  if (!target || target.kind !== "folder") return null;
  if (overHeight <= 0) return { kind: "into", folderId: target.id };
  const rel = (pointerY - overTop) / overHeight;
  if (rel < 0.28) {
    return {
      kind: "before",
      parentId: target.parentId,
      beforeId: target.id,
      targetKind: "folder",
    };
  }
  if (rel > 0.72) {
    return {
      kind: "after",
      parentId: target.parentId,
      afterId: target.id,
      targetKind: "folder",
    };
  }
  return { kind: "into", folderId: target.id };
}

function PlaylistRow({
  id,
  to,
  label,
  coverUrl,
  folderId,
  editing,
  draft,
  dropHint,
  onDraftChange,
  onCommitRename,
  onCancelRename,
  onStartRename,
  onDelete,
  onDuplicate,
  onOpenDetail,
}: {
  id: string;
  to: string;
  label: string;
  coverUrl: string | null;
  folderId: string | null;
  editing: boolean;
  draft: string;
  dropHint: "into" | "before" | "after" | null;
  onDraftChange: (v: string) => void;
  onCommitRename: () => void;
  onCancelRename: () => void;
  onStartRename: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onOpenDetail: () => void;
}) {
  const navigate = useNavigate();
  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
    id: playlistDragId(id),
    data: { kind: "playlist", id, name: label, folderId } satisfies WorkspaceDrag,
    disabled: editing,
  });
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: playlistDropId(id),
    data: { kind: "playlist", id, folderId },
  });

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      setDragRef(node);
      setDropRef(node);
    },
    [setDragRef, setDropRef],
  );

  if (editing) {
    return (
      <div className="mx-2 flex items-center gap-2 rounded-md bg-row-hover py-1 pr-2 pl-3">
        <span className="shrink-0">
          <PlaylistThumb coverUrl={coverUrl} alt={label} />
        </span>
        <InlineRenameInput
          value={draft}
          onChange={onDraftChange}
          onCommit={onCommitRename}
          onCancel={onCancelRename}
        />
      </div>
    );
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          ref={setRefs}
          data-ws-drop={playlistDropId(id)}
          className={cn(
            "group relative mx-2 flex items-center rounded-md",
            isDragging && "opacity-40",
            isOver && dropHint === "into" && "bg-row-selected ring-1 ring-accent",
          )}
          {...listeners}
          {...attributes}
        >
          {isOver && dropHint === "before" && (
            <span className="pointer-events-none absolute inset-x-2 top-0 z-10 h-0.5 bg-accent" />
          )}
          {isOver && dropHint === "after" && (
            <span className="pointer-events-none absolute inset-x-2 bottom-0 z-10 h-0.5 bg-accent" />
          )}
          <NavLink
            to={to}
            title={label}
            className={({ isActive }) =>
              cn(
                "flex min-w-0 flex-1 cursor-grab items-center gap-3 rounded-md py-2 pr-8 pl-3 text-sm font-medium transition-colors active:cursor-grabbing",
                isActive
                  ? "bg-row-hover text-text"
                  : "text-muted hover:bg-row-hover hover:text-text",
              )
            }
            onClick={(e) => {
              if (isDragging) e.preventDefault();
            }}
          >
            <PlaylistThumb coverUrl={coverUrl} alt={label} />
            <span className="truncate">{label}</span>
          </NavLink>
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem onSelect={onOpenDetail}>
          <Info className="size-3.5" /> Details
        </ContextMenuItem>
        <ContextMenuItem onSelect={() => void navigate(`${to}/explore`)}>
          <Network className="size-3.5" /> Explore in graph
        </ContextMenuItem>
        <ContextMenuItem onSelect={onStartRename}>
          <Pencil className="size-3.5" /> Rename
        </ContextMenuItem>
        <ContextMenuItem onSelect={onDuplicate}>
          <Copy className="size-3.5" /> Duplicate
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem variant="destructive" onSelect={onDelete}>
          <Trash2 className="size-3.5" /> Delete
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

function FolderRow({
  folder,
  depth,
  editing,
  draft,
  busy,
  hasChildren,
  dropHint,
  onDraftChange,
  onCommitRename,
  onCancelRename,
  onStartRename,
  onDelete,
  onDuplicate,
  onAddPlaylist,
  onAddFolder,
  onToggleCollapsed,
  onOpenDetail,
}: {
  folder: FolderNode;
  depth: number;
  editing: boolean;
  draft: string;
  busy: boolean;
  hasChildren: boolean;
  dropHint: "into" | "before" | "after" | null;
  onDraftChange: (v: string) => void;
  onCommitRename: () => void;
  onCancelRename: () => void;
  onStartRename: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onAddPlaylist: () => void;
  onAddFolder: () => void;
  onToggleCollapsed: () => void;
  onOpenDetail: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({
    id: folderDragId(folder.id),
    data: {
      kind: "folder",
      id: folder.id,
      name: folder.name,
      parentId: folder.parent_id,
    } satisfies WorkspaceDrag,
    disabled: editing,
  });
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: folderDropId(folder.id),
    data: { kind: "folder", id: folder.id, parentId: folder.parent_id },
  });

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      setDragRef(node);
      setDropRef(node);
    },
    [setDragRef, setDropRef],
  );

  if (editing) {
    return (
      <div
        className="mx-2 flex items-center gap-2 rounded-md bg-row-hover py-1 pr-2 pl-3"
        style={nestPad(depth)}
      >
        <Folder className="size-4 shrink-0 text-accent" />
        <InlineRenameInput
          value={draft}
          onChange={onDraftChange}
          onCommit={onCommitRename}
          onCancel={onCancelRename}
        />
      </div>
    );
  }

  const Chevron = folder.collapsed ? ChevronRight : ChevronDown;

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          ref={setRefs}
          data-ws-drop={folderDropId(folder.id)}
          style={nestPad(depth)}
          className={cn(
            "group relative mx-2 flex items-center rounded-md",
            isDragging && "opacity-40",
            isOver && dropHint === "into" && "bg-row-selected ring-1 ring-accent",
          )}
        >
          {isOver && dropHint === "before" && (
            <span className="pointer-events-none absolute inset-x-2 top-0 z-10 h-0.5 bg-accent" />
          )}
          {isOver && dropHint === "after" && (
            <span className="pointer-events-none absolute inset-x-2 bottom-0 z-10 h-0.5 bg-accent" />
          )}
          <button
            type="button"
            className="flex size-7 shrink-0 items-center justify-center rounded-md text-faint hover:bg-row-hover hover:text-text"
            aria-label={
              folder.collapsed ? `Expand ${folder.name}` : `Collapse ${folder.name}`
            }
            aria-expanded={!folder.collapsed}
            onClick={onToggleCollapsed}
          >
            <Chevron className={cn("size-3.5", !hasChildren && "opacity-30")} />
          </button>
          <button
            type="button"
            className="flex min-w-0 flex-1 items-center gap-2 rounded-md py-1.5 pr-2 text-left text-sm font-medium text-muted hover:text-text"
            onClick={onToggleCollapsed}
          >
            <span
              className="flex shrink-0 cursor-grab touch-none items-center active:cursor-grabbing"
              {...listeners}
              {...attributes}
              onClick={(e) => e.stopPropagation()}
            >
              <Folder className="size-4" />
            </span>
            <span className="truncate">{folder.name}</span>
          </button>
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem onSelect={onOpenDetail}>
          <Info className="size-3.5" /> Details
        </ContextMenuItem>
        <ContextMenuItem disabled={busy} onSelect={onAddPlaylist}>
          <Plus className="size-3.5" /> New playlist
        </ContextMenuItem>
        <ContextMenuItem disabled={busy} onSelect={onAddFolder}>
          <Folder className="size-3.5" /> New folder
        </ContextMenuItem>
        <ContextMenuItem onSelect={onStartRename}>
          <Pencil className="size-3.5" /> Rename
        </ContextMenuItem>
        <ContextMenuItem disabled={busy} onSelect={onDuplicate}>
          <Copy className="size-3.5" /> Duplicate
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem variant="destructive" onSelect={onDelete}>
          <Trash2 className="size-3.5" /> Delete
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

/** Workspace header doubles as the root drop target — drag here to un-nest. */
function WorkspaceHeader({
  busy,
  dragging,
  onAddFolder,
  onAddPlaylist,
}: {
  busy: boolean;
  dragging: boolean;
  onAddFolder: () => void;
  onAddPlaylist: () => void;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: ROOT_DROP_ID,
    data: { type: "root" },
  });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "mx-2 flex items-center justify-between rounded-md px-1 pt-4 pb-1 transition-colors",
        dragging && "ring-1 ring-line",
        isOver && "bg-row-selected ring-1 ring-accent",
      )}
    >
      <div className="min-w-0">
        <span className="text-xs font-semibold tracking-wider text-faint uppercase">
          Workspace
        </span>
        {isOver && (
          <span className="ml-2 text-xs font-medium text-accent">Drop to move to root</span>
        )}
        {dragging && !isOver && (
          <span className="ml-2 text-xs text-faint">Drop here for root</span>
        )}
      </div>
      <div className="flex gap-0.5">
        <Button
          variant="ghost"
          size="icon-sm"
          className="size-6"
          aria-label="New folder"
          disabled={busy}
          onClick={onAddFolder}
          title="New folder"
        >
          <Folder className="size-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          className="size-6"
          aria-label="New playlist"
          disabled={busy}
          onClick={onAddPlaylist}
          title="New playlist"
        >
          <Plus className="size-3.5" />
        </Button>
      </div>
    </div>
  );
}

/** Local folders + playlists with nest, reorder, context menus, and track drops. */
export function WorkspaceSection() {
  const navigate = useNavigate();
  const openDetail = useOpenDetail();
  const { activeDrag, dropHint, overId, registerWorkspaceHandler } = useLibraryDnd();
  const folders = useFolders();
  const playlists = useLocalPlaylists();
  const createFolder = useCreateFolder();
  const renameFolder = useRenameFolder();
  const moveFolder = useMoveFolder();
  const setCollapsed = useSetFolderCollapsed();
  const deleteFolder = useDeleteFolder();
  const duplicateFolder = useDuplicateFolder();
  const createPlaylist = useCreateLocalPlaylist();
  const renamePlaylist = useRenameLocalPlaylist();
  const movePlaylist = useMoveLocalPlaylist();
  const deletePlaylist = useDeleteLocalPlaylist();
  const duplicatePlaylist = useDuplicateLocalPlaylist();
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<Editing>(null);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete>(null);

  const roots = useMemo(() => folders.data ?? [], [folders.data]);
  const lists = useMemo(() => playlists.data ?? [], [playlists.data]);
  const rootLists = useMemo(() => lists.filter((p) => p.folder_id === null), [lists]);

  const folderById = useMemo(() => {
    const map = new Map<string, FolderNode>();
    const walk = (nodes: FolderNode[]) => {
      for (const n of nodes) {
        map.set(n.id, n);
        walk(n.children);
      }
    };
    walk(roots);
    return map;
  }, [roots]);

  const playlistById = useMemo(() => {
    const map = new Map<string, (typeof lists)[number]>();
    for (const p of lists) map.set(p.id, p);
    return map;
  }, [lists]);

  const startRename = useCallback((kind: "folder" | "playlist", id: string, name: string) => {
    setEditing({ kind, id, draft: name, original: name });
  }, []);

  const cancelRename = useCallback(() => setEditing(null), []);

  const commitRename = useCallback(() => {
    if (!editing) return;
    const next = editing.draft.trim();
    if (!next || next === editing.original) {
      setEditing(null);
      return;
    }
    if (editing.kind === "folder") {
      renameFolder.mutate({ id: editing.id, name: next });
    } else {
      renamePlaylist.mutate({ id: editing.id, name: next });
    }
    setEditing(null);
  }, [editing, renameFolder, renamePlaylist]);

  async function addFolder(parentId: string | null = null) {
    const name = nextDefaultName("New folder", flattenFolderNames(roots));
    setBusy(true);
    try {
      const created = await createFolder.mutateAsync(
        parentId ? { name, parent_id: parentId } : { name },
      );
      setEditing({ kind: "folder", id: created.id, draft: created.name, original: created.name });
    } finally {
      setBusy(false);
    }
  }

  async function addPlaylist(folderId: string | null) {
    const name = nextDefaultName(
      "New playlist",
      lists.map((p) => p.name),
    );
    setBusy(true);
    try {
      const created = await createPlaylist.mutateAsync({ name, folder_id: folderId });
      setEditing({
        kind: "playlist",
        id: created.id,
        draft: created.name,
        original: created.name,
      });
      void navigate(`/local/${created.id}`);
    } finally {
      setBusy(false);
    }
  }

  function confirmDelete() {
    if (!pendingDelete) return;
    const target = pendingDelete;
    setPendingDelete(null);
    if (target.kind === "folder") {
      deleteFolder.mutate(target.id);
    } else {
      deletePlaylist.mutate(target.id);
      void navigate("/liked");
    }
  }

  useEffect(() => {
    return registerWorkspaceHandler((event: DragEndEvent, pointerY: number) => {
      const active = event.active.data.current as WorkspaceDrag | undefined;
      const nextOverId =
        typeof event.over?.id === "string"
          ? event.over.id
          : event.over?.id != null
            ? String(event.over.id)
            : null;
      if (!active || nextOverId === null) return;

      let target:
        | { kind: "folder"; id: string; parentId: string | null }
        | { kind: "playlist"; id: string; folderId: string | null }
        | undefined;
      let overTop = 0;
      let overHeight = 0;

      if (nextOverId.startsWith("drop:folder:")) {
        const id = nextOverId.slice("drop:folder:".length);
        const folder = folderById.get(id);
        if (folder) {
          target = { kind: "folder", id, parentId: folder.parent_id };
          const el = document.querySelector(`[data-ws-drop="${nextOverId}"]`);
          if (el instanceof HTMLElement) {
            const rect = el.getBoundingClientRect();
            overTop = rect.top;
            overHeight = rect.height;
          }
        }
      } else if (nextOverId.startsWith("drop:playlist:")) {
        const id = nextOverId.slice("drop:playlist:".length);
        const pl = playlistById.get(id);
        if (pl) {
          target = { kind: "playlist", id, folderId: pl.folder_id };
          const el = document.querySelector(`[data-ws-drop="${nextOverId}"]`);
          if (el instanceof HTMLElement) {
            const rect = el.getBoundingClientRect();
            overTop = rect.top;
            overHeight = rect.height;
          }
        }
      }

      const intent = resolveWorkspaceIntent(
        nextOverId,
        pointerY,
        overTop,
        overHeight,
        target,
        false,
      );
      if (!intent || intent.kind === "tracks-onto-playlist") return;

      if (active.kind === "playlist") {
        if (intent.kind === "root") {
          if (active.folderId === null) return;
          movePlaylist.mutate({ id: active.id, folder_id: null });
          return;
        }
        if (intent.kind === "into") {
          if (intent.folderId === active.folderId) return;
          movePlaylist.mutate({ id: active.id, folder_id: intent.folderId });
          return;
        }
        if (intent.targetKind !== "playlist") {
          // Dropping a playlist on a folder edge → reparent into that folder's parent.
          if (intent.kind === "before" || intent.kind === "after") {
            const nextFolderId = intent.parentId;
            if (nextFolderId === active.folderId) return;
            movePlaylist.mutate({ id: active.id, folder_id: nextFolderId });
          }
          return;
        }
        if (intent.kind === "before") {
          if (intent.beforeId === active.id) return;
          movePlaylist.mutate({
            id: active.id,
            folder_id: intent.parentId,
            before_id: intent.beforeId,
          });
          return;
        }
        if (intent.kind === "after") {
          if (intent.afterId === active.id) return;
          movePlaylist.mutate({
            id: active.id,
            folder_id: intent.parentId,
            after_id: intent.afterId,
          });
        }
        return;
      }

      // Folder moves
      const node = findFolder(roots, active.id);
      const banned = node ? descendantIds(node) : new Set<string>();
      banned.add(active.id);

      if (intent.kind === "root") {
        if (active.parentId === null) return;
        moveFolder.mutate({ id: active.id, parent_id: null });
        return;
      }
      if (intent.kind === "into") {
        if (banned.has(intent.folderId)) return;
        if (intent.folderId === active.parentId) return;
        moveFolder.mutate({ id: active.id, parent_id: intent.folderId });
        return;
      }
      if (intent.targetKind !== "folder") return;
      if (intent.kind === "before") {
        if (intent.beforeId === active.id) return;
        if (intent.parentId !== null && banned.has(intent.parentId)) return;
        moveFolder.mutate({
          id: active.id,
          parent_id: intent.parentId,
          before_id: intent.beforeId,
        });
        return;
      }
      if (intent.kind === "after") {
        if (intent.afterId === active.id) return;
        if (intent.parentId !== null && banned.has(intent.parentId)) return;
        moveFolder.mutate({
          id: active.id,
          parent_id: intent.parentId,
          after_id: intent.afterId,
        });
      }
    });
  }, [
    folderById,
    moveFolder,
    movePlaylist,
    playlistById,
    registerWorkspaceHandler,
    roots,
  ]);

  function rowDropHint(dropId: string): "into" | "before" | "after" | null {
    if (overId !== dropId) return null;
    return dropHint;
  }

  function renderFolder(folder: FolderNode, depth: number) {
    const children = lists.filter((p) => p.folder_id === folder.id);
    const isEditing = editing?.kind === "folder" && editing.id === folder.id;
    const hasChildren = folder.children.length > 0 || children.length > 0;
    const fDrop = folderDropId(folder.id);

    return (
      <div key={folder.id} className="mb-0.5">
        <FolderRow
          folder={folder}
          depth={depth}
          editing={isEditing}
          draft={isEditing && editing ? editing.draft : folder.name}
          busy={busy}
          hasChildren={hasChildren}
          dropHint={rowDropHint(fDrop)}
          onDraftChange={(draft) => editing && setEditing({ ...editing, draft })}
          onCommitRename={commitRename}
          onCancelRename={cancelRename}
          onStartRename={() => startRename("folder", folder.id, folder.name)}
          onDelete={() => setPendingDelete({ kind: "folder", id: folder.id, name: folder.name })}
          onDuplicate={() => duplicateFolder.mutate(folder.id)}
          onAddPlaylist={() => void addPlaylist(folder.id)}
          onAddFolder={() => void addFolder(folder.id)}
          onToggleCollapsed={() =>
            setCollapsed.mutate({ id: folder.id, collapsed: !folder.collapsed })
          }
          onOpenDetail={() => openDetail({ type: "folder", id: folder.id })}
        />
        {!folder.collapsed && (
          <>
            {folder.children.map((child) => renderFolder(child, depth + 1))}
            {children.map((pl) => (
              <div key={pl.id} style={nestPad(depth + 1)}>
                <PlaylistRow
                  id={pl.id}
                  to={`/local/${pl.id}`}
                  label={pl.name}
                  coverUrl={pl.cover_url}
                  folderId={pl.folder_id}
                  editing={editing?.kind === "playlist" && editing.id === pl.id}
                  draft={editing?.id === pl.id ? editing.draft : pl.name}
                  dropHint={rowDropHint(playlistDropId(pl.id))}
                  onDraftChange={(draft) => editing && setEditing({ ...editing, draft })}
                  onCommitRename={commitRename}
                  onCancelRename={cancelRename}
                  onStartRename={() => startRename("playlist", pl.id, pl.name)}
                  onDelete={() =>
                    setPendingDelete({ kind: "playlist", id: pl.id, name: pl.name })
                  }
                  onDuplicate={() => {
                    void duplicatePlaylist.mutateAsync(pl.id).then((created) => {
                      void navigate(`/local/${created.id}`);
                    });
                  }}
                  onOpenDetail={() => openDetail({ type: "local_playlist", id: pl.id })}
                />
              </div>
            ))}
          </>
        )}
      </div>
    );
  }

  const draggingWorkspace =
    activeDrag?.kind === "folder" || activeDrag?.kind === "playlist";

  return (
    <div className="relative">
      <WorkspaceHeader
        busy={busy}
        dragging={draggingWorkspace}
        onAddFolder={() => void addFolder(null)}
        onAddPlaylist={() => void addPlaylist(null)}
      />

      {roots.map((folder) => renderFolder(folder, 0))}

      {rootLists.map((pl) => (
        <PlaylistRow
          key={pl.id}
          id={pl.id}
          to={`/local/${pl.id}`}
          label={pl.name}
          coverUrl={pl.cover_url}
          folderId={pl.folder_id}
          editing={editing?.kind === "playlist" && editing.id === pl.id}
          draft={editing?.id === pl.id ? editing.draft : pl.name}
          dropHint={rowDropHint(playlistDropId(pl.id))}
          onDraftChange={(draft) => editing && setEditing({ ...editing, draft })}
          onCommitRename={commitRename}
          onCancelRename={cancelRename}
          onStartRename={() => startRename("playlist", pl.id, pl.name)}
          onDelete={() => setPendingDelete({ kind: "playlist", id: pl.id, name: pl.name })}
          onDuplicate={() => {
            void duplicatePlaylist.mutateAsync(pl.id).then((created) => {
              void navigate(`/local/${created.id}`);
            });
          }}
          onOpenDetail={() => openDetail({ type: "local_playlist", id: pl.id })}
        />
      ))}

      {roots.length === 0 && rootLists.length === 0 && (
        <p className="px-4 py-1 text-xs text-faint">Empty — use + to add a folder or playlist</p>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title={
          pendingDelete?.kind === "folder"
            ? `Delete “${pendingDelete.name}”?`
            : `Delete “${pendingDelete?.name ?? ""}”?`
        }
        description={
          pendingDelete?.kind === "folder"
            ? "Playlists inside move to the workspace root. This cannot be undone from here yet."
            : "The playlist and its track list are removed. Tracks themselves stay in your library."
        }
        confirmLabel="Delete"
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
